import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";

const MAX_VISIBLE = 7;

const PILL_STYLE = {
  background: "#13161B",
  border: "1px solid #373A41",
  color: "#CECFD2",
} as const;

export type LogoStackItem = { id: string; name: string; logoUrl?: string | null };

interface LogoStackProps {
  items: LogoStackItem[];
  max?: number;
  showCount?: boolean;
  countLabel?: string;
}

export const LogoStack = ({ items, max = MAX_VISIBLE, showCount = true, countLabel }: LogoStackProps) => {
  if (!items.length) return <span className="text-sm text-tertiary">-</span>;

  const visible = items.slice(0, max);
  const hasMore = items.length > max;

  return (
    <div className="flex items-center gap-0.5 whitespace-nowrap">
      {showCount && (
        <span
          className="inline-flex items-center justify-center rounded-xs px-2 py-1 text-xs font-medium leading-none shrink-0"
          style={PILL_STYLE}
        >
          {countLabel ?? items.length}
        </span>
      )}
      <div className="flex gap-0.5">
        {visible.map((item) => (
          <Tooltip key={item.id} title={item.name}>
            <TooltipTrigger>
              <MerchantLogo
                name={item.name}
                logoUrl={item.logoUrl}
                size="xs"
                className="min-w-[30px] ring-2 ring-[--color-bg-primary] cursor-default rounded-xs"
              />
            </TooltipTrigger>
          </Tooltip>
        ))}
        {hasMore && (
          // Lists every item, not just the ones hidden past `max` — with max=0 (the
          // narrow-column fallback) none render at all, so "..." is the only way in.
          <Tooltip
            title={countLabel ?? `${items.length} total`}
            description={
              <ul className="list-disc list-inside">
                {items.map((item) => (
                  <li key={item.id}>{item.name}</li>
                ))}
              </ul>
            }
          >
            <TooltipTrigger>
              <span className="text-sm font-medium cursor-default" style={{ color: "#CECFD2" }}>
                ...
              </span>
            </TooltipTrigger>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
